import { X, Sparkles, Image as ImageIcon, Lock, MessageCircle } from 'lucide-react';
import type { AgentVersionPayload } from '../../types/creator';
import { normalizeOnboardingSections } from '../../utils/onboardingFormSchema';

/**
 * Lightweight, read-only landing-page preview for a draft payload.
 *
 * Renders the same structural building blocks an end user would see on the
 * public landing page (`/c/:handle/:slug`) without invoking the actual chat
 * runtime, so creators can sanity-check copy, pricing, and onboarding without
 * spinning up a real agent instance. The full chat runtime preview lands in M2.
 */
export function StudioPreviewDrawer({
  payload,
  onClose,
}: {
  payload: AgentVersionPayload;
  onClose: () => void;
}) {
  const sections = normalizeOnboardingSections(payload.onboarding_form_schema, {
    onboardingFormEnabled: payload.onboarding_form_enabled,
  });
  const totalFields = sections.reduce((n, s) => n + (s.fields?.length || 0), 0);
  const heroTitle = payload.public_title || payload.name || 'Untitled agent';
  const desc = payload.public_description || '';
  const tags = payload.tags || [];

  const pricing = payload.pricing || {};
  const pricingTiers: Array<{ label: string; sub: string }> = [];
  if (pricing.free) pricingTiers.push({ label: 'Free', sub: 'Try it without spending credits' });
  if (typeof pricing.day === 'number' && pricing.day >= 0)
    pricingTiers.push({ label: `${pricing.day} cr / day`, sub: 'Daily access' });
  if (typeof pricing.month === 'number' && pricing.month >= 0)
    pricingTiers.push({ label: `${pricing.month} cr / month`, sub: 'Monthly access' });
  if (typeof pricing.year === 'number' && pricing.year >= 0)
    pricingTiers.push({ label: `${pricing.year} cr / year`, sub: 'Yearly access' });

  return (
    <div className="fixed inset-0 z-modal-content flex" role="dialog" aria-modal="true">
      <div
        className="flex-1 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside className="w-full max-w-md sm:max-w-lg bg-base border-l border-subtle h-full overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between gap-2 p-3 border-b border-subtle bg-surface sticky top-0 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-brand shrink-0" />
            <h2 className="text-sm font-semibold text-th-primary truncate">Landing page preview</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-th-secondary hover:bg-overlay hover:text-th-primary"
            aria-label="Close preview"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          <div className="rounded-xl overflow-hidden border border-subtle bg-elevated">
            {payload.cover_media_url && payload.cover_media_type === 'image' ? (
              <img src={payload.cover_media_url} alt="" className="w-full h-40 object-cover" />
            ) : payload.cover_media_url && payload.cover_media_type === 'video' ? (
              <video src={payload.cover_media_url} className="w-full h-40 object-cover" muted playsInline />
            ) : (
              <div className="w-full h-40 flex items-center justify-center text-th-tertiary">
                <ImageIcon className="w-8 h-8" />
              </div>
            )}
            <div className="p-4 space-y-2">
              <h1 className="text-xl font-bold text-th-primary tracking-tight">{heroTitle}</h1>
              {desc ? <p className="text-sm text-th-secondary whitespace-pre-wrap">{desc}</p> : null}
              {tags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-overlay text-th-secondary"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-th-secondary">
              Pricing
            </h3>
            {pricingTiers.length === 0 ? (
              <p className="text-xs text-th-tertiary">
                No tiers configured yet \u2014 users won&apos;t be able to start this agent. Add a free tier or
                paid plan in the editor.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {pricingTiers.map((tier) => (
                  <div
                    key={tier.label}
                    className="border border-subtle rounded-lg p-3 bg-elevated flex items-center justify-between gap-3"
                  >
                    <div>
                      <p className="text-sm font-semibold text-th-primary">{tier.label}</p>
                      <p className="text-xs text-th-secondary">{tier.sub}</p>
                    </div>
                    <Lock className="w-4 h-4 text-th-tertiary" />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-th-secondary">
              Onboarding
            </h3>
            {payload.onboarding_form_enabled === false || sections.length === 0 ? (
              <p className="text-xs text-th-tertiary">
                Onboarding skipped — users go straight to chat after picking a plan.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-th-secondary">
                  {sections.length} section{sections.length === 1 ? '' : 's'} · {totalFields} field
                  {totalFields === 1 ? '' : 's'}
                </p>
                <ul className="border border-subtle rounded-lg divide-y divide-subtle bg-elevated">
                  {sections.map((s) => (
                    <li key={s.id} className="p-2.5">
                      <p className="text-xs font-semibold text-th-primary truncate">
                        {s.title || 'Untitled section'}
                      </p>
                      <ul className="text-xs text-th-secondary mt-1 space-y-0.5">
                        {(s.fields || []).map((f) => (
                          <li key={f.id} className="truncate">
                            • {f.label || f.variable_name || f.id}{' '}
                            <span className="text-th-tertiary font-mono">({f.type || '—'})</span>
                            {f.required ? <span className="text-status-error ml-1">*</span> : null}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-th-secondary">
              Capabilities
            </h3>
            <div className="flex flex-wrap gap-1.5">
              <Pill on={payload.knowledge_base_enabled === true} label="Knowledge base" />
              <Pill on={payload.file_uploads_enabled === true} label="File uploads" />
              <Pill on={payload.tool_serper === true} label="Web search" />
              <Pill on={payload.tool_tavily === true} label="Research" />
              <Pill on={payload.voice_enabled === true} label="Voice" />
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-th-secondary">
              Chat preview
            </h3>
            <div className="border border-subtle rounded-lg p-3 bg-elevated text-xs text-th-secondary flex items-start gap-2">
              <MessageCircle className="w-4 h-4 text-brand shrink-0 mt-0.5" />
              <p>
                Live conversational preview ships in the M2 Build Theater. For now you can submit the
                draft and your reviewer will run a real chat as part of approval.
              </p>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

function Pill({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={`text-[11px] px-2 py-0.5 rounded-full border ${
        on
          ? 'border-brand/40 bg-brand/10 text-brand'
          : 'border-subtle bg-elevated text-th-tertiary'
      }`}
    >
      {label}
    </span>
  );
}
